import { useEffect } from "react";
import { Link, Outlet, NavLink } from "react-router-dom";
import { useGlobalState } from "@/helpers/useGlobalState";
import { getGlobalContentitem } from "@/helpers/contentHelpers";
import type { ImageProps } from "@/types/imageProps";
import { withLocale } from "@/helpers/withLocale";
import MalencoNaw from "@/components/MalencoNaw";
import { getMsalInstance } from "@/auth/msalInstance";
import { logout } from "@/auth/auth";
import '@/css/admin.css';

type AdminLayoutProps = { titleCode?: string };
export default function AdminLayout({ titleCode }: AdminLayoutProps) {
    const { globalState } = useGlobalState();

    const pageTitle =
        titleCode
            ? getGlobalContentitem(titleCode, globalState)?.title
            : "Admin";

    const logo: ImageProps = {
        url: "Malenco_Academy_schild.png",
        alt: getGlobalContentitem("MalencoLogoImg", globalState).title,
        title: getGlobalContentitem("MalencoLogoImg", globalState).content
    };

    useEffect(() => {
        const title = pageTitle ? `${pageTitle} | Malenco Academy Admin` : "Malenco Academy Admin";
        document.title = title;
    }, [pageTitle]);

    const adminMenuItems = [
        { code: "AcademyProgrammas", to: "/admin/programs", fallback: "Programma's" },
        { code: "AcademyModules", to: "/admin/modules", fallback: "Modules" },
        { code: "AcademyOrganisaties", to: "/admin/organizations", fallback: "Organisaties" },
        { code: "AcademyBackToAcademy", to: "/", fallback: "← Terug naar Academy" }
    ];

    const navLinkClass = ({ isActive }: { isActive: boolean }) =>
        "AdminNavLink" + (isActive ? " AdminNavLink--active" : "");

    const backLabel = getGlobalContentitem("AcademyBackToAcademy", globalState);

    async function handleLogout() {
        const instance = getMsalInstance();
        await logout(instance);
    }

    function getMenuLabel(code: string, fallback: string) {
        return getGlobalContentitem(code, globalState)?.title || fallback;
    }

    function getMenuTooltip(code: string) {
        return getGlobalContentitem(code, globalState)?.content || "";
    }

    return (
        <div className="container">
            <div className="left"></div>

            <div className="center">
                {/* Topbar */}
                <div className="AdminBarSection">
                    <div className="AdminBarLogoSection">
                        <Link to={withLocale("/", globalState.Language)} title="Naar de Academy">
                            <img
                                src={"../media/img/" + logo.url}
                                alt={logo.alt}
                                title={logo.title}
                            />
                        </Link>
                    </div>

                    <div className="AdminBarNameSection">Malenco Academy</div>

                    <div className="AdminBarTitleSection">
                        <h1>{pageTitle}</h1>
                    </div>

                    <div className="AdminBarRightSection">
                        {/* Placeholder for user menu / sign out */}
                        {/* <UserMenu /> */}
                    </div>
                </div>

                {/* Body */}
                <div className="AdminShell">
                    {/* Left nav */}
                    <aside className="AdminSidebar">
                        <div className="AdminSidebarTitle">Beheer</div>
                        <nav className="AdminNav">
                            {adminMenuItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={withLocale(item.to, globalState.Language)}
                                    className={navLinkClass}
                                    title={getMenuTooltip(item.code)}
                                    end={item.to === "/"}
                                >
                                    {getMenuLabel(item.code, item.fallback)}
                                </NavLink>
                            ))}

                            {/* Uitloggen knop */}
                            <button
                                className="AdminNavLink AdminNavLink--button AdminNavLink--logout"
                                onClick={() => void handleLogout()}
                                title={getMenuTooltip("AcademyLogout")}
                            >
                                {getMenuLabel("AcademyLogout", "Uitloggen")}
                            </button>
                        </nav>

                    </aside>

                    {/* Main content */}
                    <main className="AdminMain">
                        <div className="AdminBarBreadcrumb">
                            <Link to={withLocale("/", globalState.Language)}>Academy</Link>
                            <span className="AdminBarBreadcrumbSep">/</span>
                            <span>Admin</span>
                        </div>
                        <Outlet />
                    </main>
                </div>

                {/* Footer (compact, admin context) */}
                <div className="AdminFooterSection">
                    <div className="AdminFooterContent">
                        <MalencoNaw />
                    </div>
                </div>
            </div>

            <div className="right"></div>
        </div>
    );
}
